import { z } from 'zod';
import {
  SnapshotDependency,
  SnapshotFieldDefinition,
  SnapshotLabel,
  SnapshotMilestone,
  SnapshotProject,
  SnapshotSource,
  SnapshotState,
  SnapshotTaskObject,
  TemplateSkipped,
  type TemplateSnapshot,
} from './template-snapshot.js';

/**
 * B5 (template-editor-and-dates §4.2) — валидация снапшота на входе ручек.
 *
 * Хранимая схема `TemplateSnapshot` намеренно НЕ strict (forward-compat,
 * инвариант B6) и структурных инвариантов не знает: она не поймает ни цикл
 * зависимостей, ни ссылку на несуществующий key, ни offset в 10^9 дней. Пока
 * снапшоты производил только сериализатор, этого хватало. PUT/POST принимают
 * снапшот от клиента — и без этих гвардов каждый из перечисленных случаев
 * доезжает либо до мусора в jsonb, либо до 500 в материализаторе (I-D).
 *
 * Два слоя:
 *   1. `TemplateSnapshotInput` — strict-клон схемы (unknown field → error),
 *      чтобы клиент не утащил в jsonb расширенные поля duplicate-снапшота
 *      (`state_key`, `started_at`, `members`) или произвольный мусор.
 *   2. `checkSnapshotGuards()` — чистые структурные проверки. Каждый код несёт
 *      `details` с offending keys/индексами: редактор подсвечивает строку, а не
 *      показывает «422 где-то» (i18n-инвариант №19 — ни одной freeform-строки).
 */

// ─── Слой 1: strict-клон схемы входа ─────────────────────────────────

/**
 * Strict-варианты подсхем. `.strict()` здесь — на input-схеме ручки, а не на
 * stored-entity: инвариант B6 запрещает второе, но требует первого.
 */
const SnapshotSourceInput = SnapshotSource.strict(); // zod-strict-ok: input-схема ручки
const SnapshotProjectInput = SnapshotProject.strict(); // zod-strict-ok: input-схема ручки
const SnapshotStateInput = SnapshotState.strict(); // zod-strict-ok: input-схема ручки
const SnapshotFieldDefinitionInput = SnapshotFieldDefinition.strict(); // zod-strict-ok: input-схема ручки
const SnapshotLabelInput = SnapshotLabel.strict(); // zod-strict-ok: input-схема ручки
const SnapshotMilestoneInput = SnapshotMilestone.strict(); // zod-strict-ok: input-схема ручки
const SnapshotDependencyInput = SnapshotDependency.strict(); // zod-strict-ok: input-схема ручки

/**
 * Задача идёт БЕЗ refine'ов схемы: их сообщения — русский freeform, а на
 * input-пути каждое нарушение обязано иметь машинный код с details (i18n №19).
 * Оба правила (папка без дат/оценки, paired-null оценки) переехали в гварды
 * слоя 2 — `template_snapshot_folder_has_dates` / `..._estimate_unpaired`.
 */
const SnapshotTaskInput = SnapshotTaskObject.strict(); // zod-strict-ok: input-схема ручки

export const TemplateSnapshotInput = z
  .object({
    schema_version: z.literal(2),
    source: SnapshotSourceInput.nullable().default(null),
    project: SnapshotProjectInput,
    states: z.array(SnapshotStateInput).default([]),
    field_definitions: z.array(SnapshotFieldDefinitionInput).default([]),
    labels: z.array(SnapshotLabelInput).default([]),
    entity_label_ids: z.array(z.string().uuid()).default([]),
    milestones: z.array(SnapshotMilestoneInput).default([]),
    tasks: z.array(SnapshotTaskInput).default([]),
    dependencies: z.array(SnapshotDependencyInput).default([]),
    skipped: TemplateSkipped.default({}),
  })
  .strict(); // zod-strict-ok: input-схема ручки
export type TemplateSnapshotInput = z.infer<typeof TemplateSnapshotInput>;

// ─── Слой 2: структурные гварды ──────────────────────────────────────

export const TEMPLATE_SNAPSHOT_GUARD_CODES = [
  'template_snapshot_duplicate_keys',
  'template_snapshot_parent_invalid',
  'template_snapshot_dep_invalid',
  'template_snapshot_dep_cycle',
  'template_snapshot_dep_duplicate',
  'template_snapshot_milestone_link_invalid',
  'template_snapshot_default_state_invalid',
  'template_snapshot_offsets_negative',
  'template_snapshot_offsets_inverted',
  'template_snapshot_offset_out_of_range',
  'template_snapshot_numeric_out_of_range',
  'template_snapshot_date_custom_field',
  'template_snapshot_too_large',
  /**
   * Два кода сверх таблицы §4.2. Они закрывают ровно те правила, которые в
   * stored-схеме живут freeform-refine'ами: SOLUTION требует «маппить refine'ы
   * в коды слоя 2», а кодов под них в таблице не было. Мимикрировать под
   * соседний код («инверсия offsets» для папки с датами) значило бы врать в
   * подсветке редактора.
   */
  'template_snapshot_folder_has_dates',
  'template_snapshot_estimate_unpaired',
] as const;
export type TemplateSnapshotGuardCode = (typeof TEMPLATE_SNAPSHOT_GUARD_CODES)[number];

export type TemplateSnapshotViolation = {
  code: TemplateSnapshotGuardCode;
  /** Машинные details: keys/индексы offending-элементов. Никаких строк для человека. */
  details: Record<string, unknown>;
};

/** Как в save-as-template (`TEMPLATE_TASK_CAP`) — один потолок на оба пути. */
export const TEMPLATE_TASK_CAP = 2000;
/** ±10 лет: гвард RangeError в addDays и бессмысленных планов. */
export const TEMPLATE_OFFSET_CAP = 3650;
const INT32_MAX = 2_147_483_647;
const INT32_MIN = -2_147_483_648;

/** Типы кастом-полей, чьи значения снапшот хранить не имеет права (I3). */
const DATE_FIELD_TYPES = new Set(['date', 'datetime']);

type GuardTask = {
  key: string;
  kind: string;
  parent_key: string | null;
  sort_order: number;
  start_offset_days: number | null;
  due_offset_days: number | null;
  estimate_value: string | null;
  estimate_uom: string | null;
  custom_fields: Record<string, unknown>;
};

type GuardSnapshot = {
  states: Array<{ key: string; sequence: number; is_default: boolean }>;
  field_definitions: Array<{ key: string; type: string; sequence: number }>;
  labels: Array<{ key: string }>;
  milestones: Array<{
    key: string;
    position: number;
    deadline_offset_days: number | null;
    billing_pct: string | null;
    linked_task_key: string | null;
  }>;
  tasks: GuardTask[];
  dependencies: Array<{ from_key: string; to_key: string; relation_type: string }>;
};

function pushDuplicates(values: string[], scope: string, out: TemplateSnapshotViolation[]): void {
  const seen = new Set<string>();
  const dupes: string[] = [];
  for (const v of values) {
    if (seen.has(v)) {
      if (!dupes.includes(v)) dupes.push(v);
    } else seen.add(v);
  }
  if (dupes.length > 0) {
    out.push({ code: 'template_snapshot_duplicate_keys', details: { scope, keys: dupes } });
  }
}

/**
 * Полный topo-обход по рёбрам `from_key → to_key`. Наивная проверка «встречной
 * пары» ловит только циклы длины 2 — A→B→C→A прошёл бы насквозь и упал уже в
 * материализаторе (или, хуже, создал бы вечно-заблокированный план).
 * Возвращает keys цикла, чтобы редактор подсветил все звенья.
 */
function findCycle(nodes: string[], edges: Array<[string, string]>): string[] | null {
  const adj = new Map<string, string[]>();
  for (const n of nodes) adj.set(n, []);
  for (const [from, to] of edges) {
    if (!adj.has(from)) adj.set(from, []);
    adj.get(from)?.push(to);
  }
  const WHITE = 0;
  const GREY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  for (const n of adj.keys()) color.set(n, WHITE);
  const stack: string[] = [];

  function dfs(node: string): string[] | null {
    color.set(node, GREY);
    stack.push(node);
    for (const next of adj.get(node) ?? []) {
      const c = color.get(next) ?? WHITE;
      if (c === GREY) {
        const at = stack.indexOf(next);
        return stack.slice(at === -1 ? 0 : at).concat(next);
      }
      if (c === WHITE) {
        const found = dfs(next);
        if (found) return found;
      }
    }
    stack.pop();
    color.set(node, BLACK);
    return null;
  }

  for (const n of adj.keys()) {
    if ((color.get(n) ?? WHITE) === WHITE) {
      const found = dfs(n);
      if (found) return found;
    }
  }
  return null;
}

/** Циклы в иерархии parent_key — то же самое, но по одному ребру на узел. */
function findParentCycle(tasks: GuardTask[]): string[] | null {
  const parent = new Map(tasks.map((t) => [t.key, t.parent_key]));
  for (const t of tasks) {
    const seen = new Set<string>([t.key]);
    let cur = t.parent_key;
    while (cur !== null && cur !== undefined) {
      if (seen.has(cur)) return [...seen, cur];
      seen.add(cur);
      const next = parent.get(cur);
      if (next === undefined) break; // несуществующий parent — другой код
      cur = next;
    }
  }
  return null;
}

function isDecimalStringInRange(v: string, min: number, max: number): boolean {
  if (!/^-?\d+(\.\d+)?$/.test(v)) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= min && n <= max;
}

/**
 * Структурные гварды снапшота. Чистая функция: вход — распарсенный снапшот,
 * выход — список нарушений (пустой = валиден). Сервер и редактор гоняют
 * буквально её же — дрейф клиентской и серверной валидации невозможен.
 */
export function checkSnapshotGuards(
  snapshot: TemplateSnapshot | TemplateSnapshotInput,
): TemplateSnapshotViolation[] {
  const s = snapshot as unknown as GuardSnapshot;
  const out: TemplateSnapshotViolation[] = [];

  // ── Размер ────────────────────────────────────────────────────────
  if (s.tasks.length > TEMPLATE_TASK_CAP) {
    out.push({
      code: 'template_snapshot_too_large',
      details: { task_count: s.tasks.length, cap: TEMPLATE_TASK_CAP },
    });
  }

  // ── Дубли ключей (по каждой коллекции отдельно) ───────────────────
  pushDuplicates(
    s.tasks.map((t) => t.key),
    'tasks',
    out,
  );
  pushDuplicates(
    s.states.map((x) => x.key),
    'states',
    out,
  );
  pushDuplicates(
    s.milestones.map((x) => x.key),
    'milestones',
    out,
  );
  pushDuplicates(
    s.labels.map((x) => x.key),
    'labels',
    out,
  );
  pushDuplicates(
    s.field_definitions.map((x) => x.key),
    'field_definitions',
    out,
  );

  const taskKeys = new Set(s.tasks.map((t) => t.key));

  // ── Иерархия ──────────────────────────────────────────────────────
  const badParents = s.tasks
    .filter((t) => t.parent_key !== null && !taskKeys.has(t.parent_key))
    .map((t) => t.key);
  const selfParents = s.tasks.filter((t) => t.parent_key === t.key).map((t) => t.key);
  const parentCycle = findParentCycle(s.tasks);
  if (badParents.length > 0 || selfParents.length > 0 || parentCycle) {
    out.push({
      code: 'template_snapshot_parent_invalid',
      details: {
        ...(badParents.length > 0 ? { missing_parent_keys: badParents } : {}),
        ...(selfParents.length > 0 ? { self_parent_keys: selfParents } : {}),
        ...(parentCycle ? { cycle_keys: parentCycle } : {}),
      },
    });
  }

  // ── Зависимости ───────────────────────────────────────────────────
  const danglingDeps: Array<{ index: number; from_key: string; to_key: string }> = [];
  s.dependencies.forEach((d, index) => {
    if (!taskKeys.has(d.from_key) || !taskKeys.has(d.to_key)) {
      danglingDeps.push({ index, from_key: d.from_key, to_key: d.to_key });
    }
  });
  if (danglingDeps.length > 0) {
    out.push({ code: 'template_snapshot_dep_invalid', details: { edges: danglingDeps } });
  }

  const seenEdges = new Set<string>();
  const dupEdges: Array<{ from_key: string; to_key: string }> = [];
  for (const d of s.dependencies) {
    // Дублем считается пара концов независимо от типа связи: два ребра A→B
    // материализуются в две строки-близнеца, которые потом никто не различит.
    const k = `${d.from_key} ${d.to_key}`;
    if (seenEdges.has(k)) dupEdges.push({ from_key: d.from_key, to_key: d.to_key });
    else seenEdges.add(k);
  }
  if (dupEdges.length > 0) {
    out.push({ code: 'template_snapshot_dep_duplicate', details: { edges: dupEdges } });
  }

  if (danglingDeps.length === 0) {
    const cycle = findCycle(
      [...taskKeys],
      s.dependencies.map((d) => [d.from_key, d.to_key] as [string, string]),
    );
    if (cycle) {
      out.push({ code: 'template_snapshot_dep_cycle', details: { cycle_keys: cycle } });
    }
  }

  // ── Вехи ──────────────────────────────────────────────────────────
  const badLinks = s.milestones
    .filter((m) => m.linked_task_key !== null && !taskKeys.has(m.linked_task_key))
    .map((m) => ({ key: m.key, linked_task_key: m.linked_task_key }));
  if (badLinks.length > 0) {
    out.push({
      code: 'template_snapshot_milestone_link_invalid',
      details: { milestones: badLinks },
    });
  }

  // ── Статусы ───────────────────────────────────────────────────────
  if (s.states.length > 0) {
    const defaults = s.states.filter((x) => x.is_default).map((x) => x.key);
    if (defaults.length !== 1) {
      out.push({
        code: 'template_snapshot_default_state_invalid',
        details: { default_count: defaults.length, keys: defaults },
      });
    }
  }

  // ── Смещения ──────────────────────────────────────────────────────
  const negative: string[] = [];
  const inverted: Array<{ key: string; start: number; due: number }> = [];
  const outOfRange: Array<{ key: string; offset: number }> = [];
  for (const t of s.tasks) {
    for (const off of [t.start_offset_days, t.due_offset_days]) {
      if (off === null) continue;
      if (off < 0 && !negative.includes(t.key)) negative.push(t.key);
      if (Math.abs(off) > TEMPLATE_OFFSET_CAP) outOfRange.push({ key: t.key, offset: off });
    }
    if (
      t.start_offset_days !== null &&
      t.due_offset_days !== null &&
      t.due_offset_days < t.start_offset_days
    ) {
      inverted.push({ key: t.key, start: t.start_offset_days, due: t.due_offset_days });
    }
  }
  for (const m of s.milestones) {
    const off = m.deadline_offset_days;
    if (off === null) continue;
    if (off < 0 && !negative.includes(m.key)) negative.push(m.key);
    if (Math.abs(off) > TEMPLATE_OFFSET_CAP) outOfRange.push({ key: m.key, offset: off });
  }
  if (negative.length > 0) {
    out.push({ code: 'template_snapshot_offsets_negative', details: { keys: negative } });
  }
  if (inverted.length > 0) {
    out.push({ code: 'template_snapshot_offsets_inverted', details: { tasks: inverted } });
  }
  if (outOfRange.length > 0) {
    out.push({
      code: 'template_snapshot_offset_out_of_range',
      details: { cap: TEMPLATE_OFFSET_CAP, offsets: outOfRange },
    });
  }

  // ── Папка: ни собственных дат, ни оценки (иначе гант рисует небылицу) ──
  const folderOffenders = s.tasks
    .filter(
      (t) =>
        t.kind === 'folder' &&
        (t.start_offset_days !== null || t.due_offset_days !== null || t.estimate_value !== null),
    )
    .map((t) => t.key);
  if (folderOffenders.length > 0) {
    out.push({ code: 'template_snapshot_folder_has_dates', details: { keys: folderOffenders } });
  }

  // ── Оценка: value и uom — оба или ни одного ───────────────────────
  const unpaired = s.tasks
    .filter((t) => (t.estimate_value === null) !== (t.estimate_uom === null))
    .map((t) => ({
      key: t.key,
      has_value: t.estimate_value !== null,
      has_uom: t.estimate_uom !== null,
    }));
  if (unpaired.length > 0) {
    out.push({ code: 'template_snapshot_estimate_unpaired', details: { tasks: unpaired } });
  }

  // ── Числа вне диапазонов ──────────────────────────────────────────
  const badNumeric: Array<{ scope: string; key: string; field: string; value: unknown }> = [];
  const intOk = (n: number) => Number.isInteger(n) && n >= INT32_MIN && n <= INT32_MAX;
  for (const t of s.tasks) {
    if (!intOk(t.sort_order)) {
      badNumeric.push({ scope: 'tasks', key: t.key, field: 'sort_order', value: t.sort_order });
    }
  }
  for (const st of s.states) {
    if (!intOk(st.sequence)) {
      badNumeric.push({ scope: 'states', key: st.key, field: 'sequence', value: st.sequence });
    }
  }
  for (const f of s.field_definitions) {
    if (!intOk(f.sequence)) {
      badNumeric.push({
        scope: 'field_definitions',
        key: f.key,
        field: 'sequence',
        value: f.sequence,
      });
    }
  }
  for (const m of s.milestones) {
    if (!intOk(m.position)) {
      badNumeric.push({ scope: 'milestones', key: m.key, field: 'position', value: m.position });
    }
    if (m.billing_pct !== null && !isDecimalStringInRange(m.billing_pct, 0, 100)) {
      badNumeric.push({
        scope: 'milestones',
        key: m.key,
        field: 'billing_pct',
        value: m.billing_pct,
      });
    }
  }
  if (badNumeric.length > 0) {
    out.push({ code: 'template_snapshot_numeric_out_of_range', details: { fields: badNumeric } });
  }

  // ── date-кастом-поля (обход I3 через PUT) ─────────────────────────
  const dateFieldKeys = new Set(
    s.field_definitions.filter((f) => DATE_FIELD_TYPES.has(f.type)).map((f) => f.key),
  );
  if (dateFieldKeys.size > 0) {
    const offenders: Array<{ key: string; field_key: string }> = [];
    for (const t of s.tasks) {
      for (const [fk, v] of Object.entries(t.custom_fields ?? {})) {
        if (dateFieldKeys.has(fk) && v !== null) offenders.push({ key: t.key, field_key: fk });
      }
    }
    if (offenders.length > 0) {
      out.push({ code: 'template_snapshot_date_custom_field', details: { values: offenders } });
    }
  }

  return out;
}
